import React from 'react';
import PropTypes from 'prop-types';
import {action, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import Cookies from 'js-cookie';
import autobind from 'autobind-decorator';
import {Link} from 'react-router';

import {withI18n} from 'spa/Components/Context';
import {browserOnly} from 'spa/Components/EnvSplit';

import 'spa/Components/cookie-banner.less';

const COOKIE_SCROLL_OFFSET = 300;

@browserOnly
@inject(({store: {tracker}}) => ({
  tracker,
}))
@withI18n
@observer
class CookieBanner extends React.Component {
  static propTypes = {
    cookieName: PropTypes.string.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  }

  UNSAFE_componentWillMount() {
    const {cookieName} = this.props;

    this.isDismissed = Cookies.get(cookieName);
    // Add the scroll event listener
    window.addEventListener('load', () => {
      this.initialPosition = this.supportsPageOffset ?
        window.pageYOffset :
        document.documentElement.scrollTop || document.body.scrollTop;

      if (window.addEventListener) {
        window.addEventListener('scroll', this.onScroll);
      } else if (window.attachEvent) {
        window.attachEvent('scroll', this.onScroll);
      }
    });
  }

  componentDidMount() {
    this.supportsPageOffset = window.pageYOffset !== undefined;

    this.listeners = [
      reaction(
        () => this.isDismissed,
        (isDismissed) => {
          if (isDismissed) {
            // remove our scroll listener as component doesn't unmount on removal
            if (window.removeEventListener) {
              window.removeEventListener('scroll', this.onScroll);
            } else if (window.detachEvent) {
              window.detachEvent('scroll', this.onScroll);
            }
          }
        },
        {
          name: 'Remove scroll listener - Cookie Banner',
        },
      ),
    ];
  }

  componentWillUnmount() {
    // remove our scroll listener
    if (window.removeEventListener) {
      window.removeEventListener('scroll', this.onScroll);
    } else if (window.detachEvent) {
      window.detachEvent('scroll', this.onScroll);
    }

    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @autobind
  @action('onScroll - CookieBanner')
  onScroll() {
    const currentPos = this.supportsPageOffset ?
      window.pageYOffset :
      document.documentElement.scrollTop || document.body.scrollTop;
    // Remove our cookie banner if the user has scrolled far enough on the site
    if (
      this.initialPosition !== undefined &&
      (
        currentPos <= this.initialPosition - COOKIE_SCROLL_OFFSET ||
        currentPos >= this.initialPosition + COOKIE_SCROLL_OFFSET
      )
    ) {
      this.dismissCookie('Scroll');
    }
  }

  @autobind
  @action('onClickDismiss - CookieBanner')
  onClickDismiss(event) {
    event.preventDefault();

    this.dismissCookie();
  }

  @autobind
  @action('dismissCookie - CookieBanner')
  dismissCookie(dismissType) {
    const {cookieName, tracker} = this.props;

    tracker.track(`Cookie Banner Dismissed ${dismissType}`, {
      category: 'Cookie Banner',
    });

    Cookies.set(cookieName, 'true', { expires: 365 });
    this.isDismissed = true;
  }

  @observable initialPosition;
  @observable isDismissed = false;

  render() {
    const {isDismissed, onClickDismiss} = this;
    const {i18n} = this.context;

    if (!isDismissed) {
      return (
        <div className="cookie-banner__container">
          <div className="cookie-banner">
            <a
              className="cookie-banner__dismiss"
              href="#dismiss-banner"
              onClick={onClickDismiss}
              title="Dismiss"
            />
            <div className="cookie-banner__content">
              <p className="cookie-banner__title">{i18n._x('This website uses cookies', 'smart banner title')}</p>
              <p className="cookie-banner__text">{i18n.__react('We use cookies to personalise and provide certain functionality across the Urban websites. They also help us analyse how people use our site and help us to improve it. By continuing to use this site you are agreeing to our use of cookies as defined in our %(cookiePolicyLink)s', {
                cookiePolicyLink: <Link className="cookie-banner__anchor" to={`/${i18n.lc}/cookies`} >{i18n._x('cookies policy', 'Cookie banner link text')}</Link>,
              }, 'cookie banner text')}</p>
            </div>
            <div className="cookie-banner__close-container">
              <button
                className="cookie-banner__close-button button button--success button--micro"
                data-qa="cookie-banner"
                title={i18n._x('OK', 'Cookies accept button')}
                onClick={onClickDismiss}
              >
                {i18n._x('OK', 'Cookies accept button')}
              </button>

            </div>
          </div>
        </div>
      );
    }

    return null;
  }
}

export default CookieBanner;



// WEBPACK FOOTER //
// ./spa/Components/CookieBanner.jsx